import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import ContainerBox from './ContainerBox';
import './TargetSelectionContainer.css';
import './DataAnalysisContainer.css'; // Import to reuse correlation styles

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const TargetSelectionContainer = ({
  analysis,
  visible,
  userContextChangeCounter,
  droppedColumns,
  onTargetSelected,
  onReadyClick,
  onRefreshModelStatus
}) => {
  const [targetData, setTargetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [topCorrelations, setTopCorrelations] = useState([]);
  const [isStale, setIsStale] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [originalTargetColumns, setOriginalTargetColumns] = useState(null); // Track original columns from backend
  const [targetModelStatus, setTargetModelStatus] = useState({}); // Map of target_column -> model status

  const isReady = selectedTarget && !loading;

  // Filter target columns to exclude dropped columns and de-duplicate by logical key
  // (actual_column if present, otherwise name). This prevents the same logical target
  // from appearing multiple times in the UI, which previously caused multiple cards
  // to appear selected when only one target was chosen.
  const filteredTargetColumns =
    targetData?.target_columns
      ?.filter(col => !droppedColumns?.includes(col.name))
      .reduce((acc, col) => {
        const key = col.actual_column || col.name;
        if (!acc.seen.has(key)) {
          acc.seen.add(key);
          acc.items.push(col);
        }
        return acc;
      }, { seen: new Set(), items: [] }).items || [];

  const [hasPredictions, setHasPredictions] = useState({}); // Map of target_column -> has_predictions

  // Define fetchModelStatus early so it can be used in useEffect hooks
  const fetchModelStatus = useCallback(async () => {
    if (!analysis?.datasets?.[0]?.id) return;

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getAllTargetsModelStatus(analysis.datasets[0].id);
      setTargetModelStatus(response.data.target_status || {});
      setHasPredictions(response.data.has_predictions || {});
    } catch (err) {
      console.error('Failed to fetch model status:', err);
      setTargetModelStatus({});
      setHasPredictions({});
    }
  }, [analysis?.datasets]);

  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id) {
      if (!targetData) {
        fetchTargetData();
      }
      checkStaleStatus();
      fetchModelStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: avoid re-running on fn identity changes
  }, [visible, analysis?.datasets, fetchModelStatus]);

  // Expose fetchModelStatus to parent via callback
  useEffect(() => {
    if (onRefreshModelStatus) {
      onRefreshModelStatus(fetchModelStatus);
    }
  }, [onRefreshModelStatus, fetchModelStatus]);

  // Load persisted target selection when component becomes visible
  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id && targetData && !selectedTarget) {
      loadPersistedTargetSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: load once when visible/datasets/targetData/selectedTarget allow
  }, [visible, analysis?.datasets, targetData, selectedTarget]);

  // Check stale status when user context changes
  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id && userContextChangeCounter > 0) {
      checkStaleStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only re-run when counter changes
  }, [userContextChangeCounter]);

  // Check stale status when dropped columns change
  // Only mark as stale if a column is ADDED BACK that wasn't in the original list
  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id && droppedColumns && originalTargetColumns) {
      // Get the current available columns (not dropped)
      const currentAvailableColumns = targetData?.target_columns
        ?.filter(col => !droppedColumns.includes(col.name))
        .map(col => col.name) || [];

      // Get the original column names
      const originalColumnNames = originalTargetColumns.map(col => col.name);

      // Check if any current available column was NOT in the original list
      const hasNewColumn = currentAvailableColumns.some(
        colName => !originalColumnNames.includes(colName)
      );

      if (hasNewColumn) {
        // A column was added back that wasn't in the original list - mark as stale
        checkStaleStatus();
      }

      // Always refresh model status when columns change (models become stale)
      fetchModelStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: stable deps to avoid unnecessary reruns
  }, [droppedColumns, fetchModelStatus, visible, analysis?.datasets, originalTargetColumns, targetData?.target_columns]);

  const checkStaleStatus = async () => {
    if (!analysis?.datasets?.[0]?.id) return;

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getArtifactStatus(analysis.datasets[0].id, 'target_selection');
      const status = response.data;

      setIsStale(status.is_stale);
    } catch (err) {
      console.error('Failed to check stale status:', err);
    }
  };

  const handleRefresh = async () => {
    if (!analysis?.datasets?.[0]?.id) return;

    setRefreshing(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');
      await apiService.refreshTargetSelectionArtifacts(analysis.datasets[0].id);

      // After refresh, fetch data again
      const response = await apiService.getTargetSelection(analysis.datasets[0].id);
      setTargetData(response.data);

      // Update original columns to the new fetched data (after refresh, this becomes the new baseline)
      setOriginalTargetColumns(response.data.target_columns);

      setIsStale(false);
    } catch (err) {
      console.error('Failed to refresh target selection:', err);
      setError('Failed to refresh target selection. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchTargetData = async () => {
    if (!analysis?.datasets?.[0]?.id) return;

    setLoading(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getTargetSelection(analysis.datasets[0].id);
      setTargetData(response.data);

      // Store the original target columns when first fetched
      if (!originalTargetColumns) {
        setOriginalTargetColumns(response.data.target_columns);
      }
    } catch (err) {
      setError('Failed to load target selection data. Please try again.');
      console.error('Target selection error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPersistedTargetSelection = async () => {
    if (!analysis?.datasets?.[0]?.id) return;

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getTargetSelectionPersist(analysis.datasets[0].id);
      const persistedTargetName = response.data?.target_selection;

      if (persistedTargetName && targetData) {
        // Find the target in our current data by name (use filtered list)
        const foundTarget = filteredTargetColumns.find(col => (col.actual_column || col.name) === persistedTargetName);
        if (foundTarget) {
          setSelectedTarget(foundTarget);
          calculateTopCorrelations(foundTarget.actual_column || foundTarget.name);
          onTargetSelected?.(foundTarget);
        } else if (droppedColumns?.includes(persistedTargetName)) {
          // The persisted target was dropped, clear selection
          setSelectedTarget(null);
          setTopCorrelations([]);
          onTargetSelected?.(null);
        }
      }
    } catch (err) {
      console.error('Failed to load persisted target selection:', err);
      // Don't show error to user, just fail silently
    }
  };

  const saveTargetSelection = async (target) => {
    if (!analysis?.datasets?.[0]?.id) return;

    try {
      const { apiService } = await import('../services/api');
      // Only save the target name, not the full object
      const targetName = target ? (target.actual_column || target.name) : null;
      await apiService.setTargetSelectionPersist(analysis.datasets[0].id, targetName);
    } catch (err) {
      console.error('Failed to save target selection:', err);
      // Don't show error to user, just fail silently
    }
  };

  const handleTargetSelect = (targetColumn) => {
    const selectedKey = selectedTarget?.actual_column || selectedTarget?.name;
    const clickedKey = targetColumn.actual_column || targetColumn.name;
    if (selectedKey === clickedKey) {
      // Deselect if clicking the same target
      setSelectedTarget(null);
      setTopCorrelations([]);
      onTargetSelected?.(null);
      saveTargetSelection(null); // Save deselection
    } else {
      setSelectedTarget(targetColumn);
      calculateTopCorrelations(clickedKey);
      onTargetSelected?.(targetColumn);
      saveTargetSelection(targetColumn); // Save selection
    }
  };

  const calculateTopCorrelations = (targetColumnName) => {
    if (!targetData?.correlation_matrix) {
      setTopCorrelations([]);
      return;
    }

    // Find correlations for the selected target
    const correlations = [];
    const seenPairs = new Set();

    // The correlation matrix comes as a dictionary with string keys like "col1,col2"
    for (const [pairKey, correlation] of Object.entries(targetData.correlation_matrix)) {
      // Parse the key which is in format "col1,col2"
      const [col1, col2] = pairKey.split(',').map(s => s.trim());

      // Skip self-correlations
      if (col1 === col2) continue;

      // Only process if one of the columns is our target
      if (col1 === targetColumnName || col2 === targetColumnName) {
        // Determine the other feature (not the target)
        const otherFeature = col1 === targetColumnName ? col2 : col1;

        // Create a normalized pair key to avoid duplicates (always put target first)
        const normalizedPair = `${targetColumnName},${otherFeature}`;

        // Skip if we've already seen this pair
        if (seenPairs.has(normalizedPair)) continue;
        seenPairs.add(normalizedPair);

        correlations.push({
          feature1: targetColumnName,
          feature2: otherFeature,
          correlation: correlation,
          correlation_raw: correlation
        });
      }
    }

    // Sort by correlation strength and take top 5
    correlations.sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));
    setTopCorrelations(correlations.slice(0, 5));
  };

  const createCorrelationGaugeData = (correlation) => {
    const absCorr = Math.abs(correlation.correlation_raw);
    const remaining = 1 - absCorr;
    
    // Color based on correlation strength: red (low) to green (high)
    const red = Math.round(255 * (1 - absCorr));
    const green = Math.round(200 * absCorr); // Reduced from 255 to 200 for less bright green
    const color = `rgb(${red}, ${green}, 0)`;
    
    return {
      labels: ['Correlation', 'Remaining'],
      datasets: [{
        data: [absCorr, remaining],
        backgroundColor: [color, 'rgba(229, 231, 235, 0.3)'],
        borderWidth: 0,
        cutout: '70%',
      }]
    };
  };

  const gaugeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    }
  };

  if (!visible) return null;

  return (
    <ContainerBox
      title="Select what to predict"
      isReady={isReady}
      readyLabel="Train Prediction Model"
      onReadyClick={onReadyClick}
      isStale={isStale}
      onRefreshClick={handleRefresh}
      refreshing={refreshing}
    >
      {loading && (
        <div className="target-loading">
          <div className="loading-spinner"></div>
          <p>Loading target selection options...</p>
        </div>
      )}

      {error && (
        <div className="target-error">
          <p>{error}</p>
          <button className="btn btn-primary btn-sm" onClick={fetchTargetData}>
            Retry
          </button>
        </div>
      )}

      {targetData && (
        <div className="target-selection-content">

          {/* Target Columns List */}
          <div className="target-columns-section">
            <div className="target-columns-scroll">
              {filteredTargetColumns.map((column) => {
                const modelKey = column.actual_column || column.name;
                const modelStatus = targetModelStatus[modelKey];
                // eslint-disable-next-line no-unused-vars -- reserved for future UI (badges/indicators)
                const hasReadyModel = modelStatus === 'ready';
                // eslint-disable-next-line no-unused-vars -- reserved for future UI (badges/indicators)
                const hasStaleModel = modelStatus === 'stale';

                return (
                  <div
                    key={modelKey}
                    className={`target-column-card ${
                      (selectedTarget?.actual_column || selectedTarget?.name) === modelKey
                        ? 'target-column-card--selected'
                        : ''
                    }`}
                    onClick={() => handleTargetSelect(column)}
                  >
                    <h4 className="target-column-card__name">
                      {column.display_name || column.name}
                      {column.is_engineered && !column.name.includes('(Forecast)') && (
                        <span className="target-badge target-badge--engineered" title="Engineered target for forecasting">Forecast</span>
                      )}
                      {hasPredictions[modelKey] && (
                        <span className="target-prediction-indicator" title="Predictions available"></span>
                      )}
                    </h4>
                    <p className="target-column-card__description">{column.use_case}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Correlations - Reusing DataAnalysisContainer structure */}
          {selectedTarget && topCorrelations.length > 0 && (
            <div className="analysis-section">
              <h3 className="analysis-section__title">
                Top Correlations with "{selectedTarget.name}"
              </h3>
              <div className="correlations-grid">
                {topCorrelations.map((corr, index) => {
                  const gaugeData = createCorrelationGaugeData(corr);
                  return (
                    <div key={index} className="correlation-card">
                      <div className="correlation-card__chart">
                        <Doughnut data={gaugeData} options={gaugeOptions} />
                        <div className="correlation-card__value">
                          {Math.abs(corr.correlation_raw).toFixed(2)}
                        </div>
                      </div>
                      <div className="correlation-card__labels">
                        <div className="correlation-card__feature">{corr.feature2}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Conditional note for low correlations */}
              {(() => {
                const avgCorrelation = topCorrelations.reduce((sum, corr) => sum + Math.abs(corr.correlation_raw), 0) / topCorrelations.length;
                return avgCorrelation >= 0.1 && avgCorrelation <= 0.5 ? (
                  <div className="correlation-note">
                    <p>
                      Note that only because the correlation between the scores between single columns and the selected target is rather low, training an accurate model could still be possible. This is due to potentially more complex relationships in the data. Proceed to the next section to find out!
                    </p>
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>
      )}
    </ContainerBox>
  );
};

export default TargetSelectionContainer;
